/**
 * AddStockPopover — Compact Material Receipt form inside a Radix Popover.
 *
 * Renders a trigger button ("+ Add stock") and a popover with warehouse,
 * qty, and rate fields. Creates a Draft Stock Entry via useCreateStockEntry
 * and refreshes stock data inline — no page navigation.
 */

import { useState, useRef, useCallback } from "react";
import { Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { LinkFieldSearch } from "@/components/filter-builder/LinkFieldSearch";
import { useCreateStockEntry } from "@/features/stock/api/useCreateStockEntry";
import { SUB_TABLE_ACTION_BUTTON_CLASS } from "./useTableStyles";

interface AddStockPopoverProps {
  itemCode: string;
  onCreated: () => void;
}

export function AddStockPopover({ itemCode, onCreated }: AddStockPopoverProps) {
  const [open, setOpen] = useState(false);
  const [warehouse, setWarehouse] = useState<string | null>(null);
  const [qty, setQty] = useState("1");
  const [rate, setRate] = useState("");

  const qtyRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const mutation = useCreateStockEntry({
    navigateOnSuccess: false,
    itemCode,
    onSuccess: () => {
      toast.success("Stock entry created (Draft)");
      setOpen(false);
      onCreated();
    },
  });

  const resetForm = useCallback(() => {
    setWarehouse(null);
    setQty("1");
    setRate("");
  }, []);

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      setOpen(nextOpen);
      if (!nextOpen) {
        resetForm();
      }
    },
    [resetForm],
  );

  const parsedQty = parseFloat(qty);
  const canSubmit = !!warehouse && parsedQty > 0 && !mutation.isPending;

  const handleSubmit = useCallback(
    (e?: React.FormEvent) => {
      if (e) e.preventDefault();
      if (!warehouse || parsedQty <= 0) return;

      const parsedRate = parseFloat(rate) || 0;

      mutation.mutate(
        {
          stock_entry_type: "Material Receipt",
          docstatus: 0,
          to_warehouse: warehouse,
          items: [
            {
              item_code: itemCode,
              qty: parsedQty,
              t_warehouse: warehouse,
              basic_rate: parsedRate,
            },
          ],
        },
        {
          onError: (err) => {
            toast.error(err.message || "Failed to create stock entry");
          },
        },
      );
    },
    [warehouse, qty, rate, itemCode, parsedQty, mutation],
  );

  const inputClass = cn(
    "w-full bg-transparent border border-neutral-300 dark:border-neutral-600",
    "rounded-xs px-2.5 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500",
    "disabled:opacity-50",
  );

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          ref={triggerRef}
          className={SUB_TABLE_ACTION_BUTTON_CLASS}
        >
          <Plus className="h-3 w-3" /> Add stock
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="bottom"
        className="w-80 p-4"
        data-inline-form-open
      >
        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          {/* Header */}
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Add Stock</span>
            <span className="text-xs text-muted-foreground">Material Receipt</span>
          </div>

          {/* Warehouse */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-muted-foreground">
              Warehouse
            </label>
            <LinkFieldSearch
              doctype="Warehouse"
              value={warehouse}
              onChange={(v) => {
                setWarehouse(v);
                setTimeout(() => qtyRef.current?.focus(), 50);
              }}
              placeholder="Select warehouse..."
              className="w-full"
              disabled={mutation.isPending}
            />
          </div>

          {/* Qty + Rate row */}
          <div className="grid grid-cols-2 gap-2">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">
                Quantity
              </label>
              <input
                ref={qtyRef}
                type="number"
                min="0.01"
                step="any"
                value={qty}
                onChange={(e) => setQty(e.target.value)}
                disabled={mutation.isPending}
                className={cn(inputClass, "tabular-nums")}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">
                Rate (optional)
              </label>
              <input
                type="number"
                step="0.01"
                value={rate}
                onChange={(e) => setRate(e.target.value)}
                placeholder="0.00"
                disabled={mutation.isPending}
                className={cn(inputClass, "tabular-nums")}
              />
            </div>
          </div>

          {/* Submit */}
          <Button
            type="submit"
            size="sm"
            className="w-full"
            disabled={!canSubmit}
          >
            {mutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                Creating...
              </>
            ) : (
              "Create Draft"
            )}
          </Button>
        </form>
      </PopoverContent>
    </Popover>
  );
}
